import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { UntypedFormGroup, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, lastValueFrom, merge, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import {
  NgbActiveModal,
  NgbModal,
  NgbTypeaheadSelectItemEvent,
  NgbTypeahead,
} from '@ng-bootstrap/ng-bootstrap';

import { faTimes, faBold } from '@fortawesome/free-solid-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { QuillEditorComponent } from 'ngx-quill';

import { Contact } from '../contact';
import { ContactService } from '../contact.service';
import { EmailService } from '../email.service';
import { SettingService } from '../setting.service';
import { AlertService } from '../alert.service';
import { LogService } from '../log.service';
import { EmailLog } from '../email-log';
import { EmailViewComponent } from '../email-view/email-view.component';
import { InvoiceService } from '../invoice.service';
import { FormatDateTimePipe } from '../format-date-time.pipe';
import { EmailTemplate, EmailTemplateType } from '../email-template';

@Component({
  selector: 'app-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.css'],
  imports: [
    ReactiveFormsModule,
    NgIf,
    NgbTypeahead,
    NgFor,
    FaIconComponent,
    QuillEditorComponent,
    FormatDateTimePipe,
  ],
})
export class SendEmailComponent implements OnInit {
  @Input() type: string;
  @Input() subject: string;
  @Input() showAttachments: boolean = false;
  @Input() clientId: string;
  @Input() projectId: string;
  @Input() proposalId: string;
  @Input() invoiceId: string;
  @Input() paymentId: string;

  @Output() emailSent: EventEmitter<void> = new EventEmitter<void>();

  sendEmailForm: UntypedFormGroup;

  contacts: Contact[] = [];
  emailLogs: EmailLog[] = [];

  emailTemplates: EmailTemplate[] = [];
  selectedEmailTemplate: EmailTemplate = null;

  private emailRegex: RegExp =
    /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

  toEmailAddresses: string[] = [];
  ccEmailAddresses: string[] = [];

  toExistingEmailAddresses: string[] = [];
  toEmailAddressTypeaheadInput: (text$: Observable<string>) => Observable<string[]>;
  toEmailAddressTypeaheadInputFocus$ = new Subject<string>();

  ccExistingEmailAddresses: string[] = [];
  ccEmailAddressTypeaheadInput: (text$: Observable<string>) => Observable<string[]>;
  ccEmailAddressTypeaheadInputFocus$ = new Subject<string>();

  emailBody: any = '';

  faTimes = faTimes;
  faBold = faBold;

  constructor(
    private settingService: SettingService,
    private contactService: ContactService,
    private invoiceService: InvoiceService,
    private emailService: EmailService,
    private alertService: AlertService,
    private logService: LogService,
    private formBuilder: FormBuilder,
    private modalService: NgbModal,
    public modal: NgbActiveModal
  ) {}

  ngOnInit(): void {
    this.sendEmailForm = this.formBuilder.group({
      emailTemplateId: [''],
      subject: [''],
      body: [''],
    });

    this.getContactsForClient();
  }

  private getContactsForClient(): void {
    const params = {
      searchTerm: null,
      sortBy: 'contact_first_name',
      sortDirection: 'ASC',
      limit: null,
      offset: null,
      'filter:client_id': JSON.stringify([this.clientId ? this.clientId : null]),
    };

    if (!this.clientId) {
      delete params['filter:client_id'];
    }

    this.contactService.getContacts(params).subscribe((res) => {
      this.contacts = res.contacts;
      this.prepareToEmailAddressTypeahead();
      this.prepareCcEmailAddressTypeahead();

      if (this.clientId) {
        this.setSendEmailFormValues();
      }

      this.getEmailTemplates();

      if (this.type !== 'GENERAL') {
        this.getFilteredEmailLogs();
      }
    });
  }

  private getEmailTemplates(): void {
    const currentType = this.normalizeTemplateType(this.type);

    const requests: Observable<EmailTemplate[]>[] = [];

    if (currentType && currentType !== 'GENERAL' && currentType !== 'GENERIC') {
      requests.push(this.settingService.getEmailTemplates(currentType));
    }

    requests.push(this.settingService.getEmailTemplates('GENERIC'));

    forkJoin(requests).subscribe({
      next: (results) => {
        const templates = results.flat();

        this.emailTemplates = this.removeDuplicateTemplates(templates).sort((a, b) => {
          if (a.type === currentType && b.type !== currentType) return -1;
          if (a.type !== currentType && b.type === currentType) return 1;
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return a.name.localeCompare(b.name);
        });

        const defaultTemplate =
          this.emailTemplates.find(
            (template) => template.type === currentType && template.isDefault
          ) ||
          this.emailTemplates.find(
            (template) => template.type === 'GENERIC' && template.isDefault
          ) ||
          this.emailTemplates[0];

        if (defaultTemplate) {
          this.sendEmailForm.controls.emailTemplateId.setValue(defaultTemplate.id);
          this.applyEmailTemplate(defaultTemplate);
        }
      },
      error: () => {
        this.alertService.showErrorAlert('Unable to load email templates');
      },
    });
  }

  emailTemplateSelected(templateId: string): void {
    if (!templateId) {
      this.clearSelectedTemplate();
      return;
    }

    const selectedTemplate = this.emailTemplates.find(
      (template) => template.id === templateId
    );

    if (!selectedTemplate) return;

    this.applyEmailTemplate(selectedTemplate);
  }

  private clearSelectedTemplate(): void {
    this.selectedEmailTemplate = null;

    this.sendEmailForm.patchValue({
      emailTemplateId: '',
      subject: '',
      body: '',
    });
  }

  private async applyEmailTemplate(template: EmailTemplate): Promise<void> {
    this.selectedEmailTemplate = template;

    const subject = template.subject || this.subject || '';
    const body = await this.replaceTemplateTokens(template.body || '');

    this.sendEmailForm.patchValue({
      subject,
      body,
    });
  }

  private async replaceTemplateTokens(template: string): Promise<string> {
    let body = template || '';

    const primaryContact = this.contacts.find((contact) => contact.isPrimary);

    if (primaryContact) {
      body = body.replaceAll('##FIRST_NAME##', primaryContact.firstName || '');
      body = body.replaceAll('{{client}}', primaryContact.firstName || '');
    }

    if (this.paymentId) {
      const params = {
        searchTerm: null,
        sortBy: 'i.invoice_number',
        sortDirection: 'DESC',
        limit: null,
        offset: null,
        'filter:proj.project_id': JSON.stringify([this.projectId]),
      };

      const invoicePricing = await lastValueFrom(
        this.invoiceService.getInvoicePricing(this.invoiceId)
      );

      const invoices = await lastValueFrom(this.invoiceService.getInvoices(params));

      const currentPayment = invoicePricing.payments.find((payment) => {
        return payment.id === this.paymentId;
      });

      if (currentPayment) {
        const proposalBalance = Number(
          currentPayment.amount +
            invoicePricing.proposalBalance -
            this.getTotalPaidAmount(invoices)
        );

        body = body.replaceAll(
          '##AMOUNT_PAID##',
          `$${currentPayment.amount.toFixed(2)}`
        );

        body = body.replaceAll(
          '##INVOICE_BALANCE##',
          `$${invoicePricing.invoice.amountDue.toFixed(2)}`
        );

        body = body.replaceAll(
          '##PROPOSAL_BALANCE##',
          `$${proposalBalance.toFixed(2)}`
        );
      }
    }

    return body;
  }

  private normalizeTemplateType(type: string): EmailTemplateType | 'GENERAL' {
    const normalized = type?.toUpperCase();

    switch (normalized) {
      case 'PROPOSAL':
      case 'INVOICE':
      case 'PAYMENT':
      case 'REVIEW':
      case 'NOTIFICATION':
      case 'GENERIC':
        return normalized as EmailTemplateType;
      case 'GENERAL':
        return 'GENERAL';
      default:
        return 'GENERAL';
    }
  }

  private removeDuplicateTemplates(templates: EmailTemplate[]): EmailTemplate[] {
    const map = new Map<string, EmailTemplate>();

    for (const template of templates) {
      map.set(template.id, template);
    }

    return Array.from(map.values());
  }

  getTemplateTypeLabel(type: string): string {
    switch (type) {
      case 'PROPOSAL':
        return 'Proposal';
      case 'INVOICE':
        return 'Invoice';
      case 'PAYMENT':
        return 'Payment';
      case 'REVIEW':
        return 'Review';
      case 'NOTIFICATION':
        return 'Notification';
      case 'GENERIC':
        return 'Generic';
      default:
        return type;
    }
  }

  getTemplateOptionLabel(template: EmailTemplate): string {
    const defaultLabel = template.isDefault ? 'Default' : '';
    const typeLabel = this.getTemplateTypeLabel(template.type);

    return [template.name, defaultLabel, typeLabel].filter(Boolean).join(' - ');
  }

  getTotalPaidAmount(data: any): number {
    if (!data?.invoices || !Array.isArray(data.invoices)) return 0;

    return data.invoices.reduce(
      (sum, invoice) => sum + (invoice.totalPaymentAmount || 0),
      0
    );
  }

  getFilteredEmailLogs(): void {
    let referenceId = null;

    switch (this.type) {
      case 'PROPOSAL':
        referenceId = this.proposalId;
        break;
      case 'INVOICE':
        referenceId = this.invoiceId;
        break;
      case 'PAYMENT':
        referenceId = this.paymentId;
        break;
      case 'REVIEW':
        referenceId = this.projectId;
        break;
      case 'GENERAL':
        break;
      default:
        this.alertService.showWarningAlert('Unknown Email Type');
        break;
    }

    if (referenceId) {
      this.logService.getFilteredEmailLogs(this.type, referenceId).subscribe((logs) => {
        this.emailLogs = logs;
      });
    }
  }

  openEmailViewModal(emailLog: EmailLog): void {
    const modalRef = this.modalService.open(EmailViewComponent);
    modalRef.componentInstance.emailLog = emailLog;
  }

  private prepareToEmailAddressTypeahead(): void {
    this.toEmailAddressTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      const inputFocus$ = this.toEmailAddressTypeaheadInputFocus$;

      this.toExistingEmailAddresses.length = 0;

      for (const contact of this.contacts) {
        if (contact.email != null) {
          this.toExistingEmailAddresses.push(contact.email);
        }
      }

      return merge(debouncedText$, inputFocus$).pipe(
        map((term) => {
          return (term.length === 0
            ? this.toExistingEmailAddresses
            : this.toExistingEmailAddresses.filter((element) => {
                return element.toLowerCase().indexOf(term.toLowerCase()) > -1;
              })
          ).slice(0, 10);
        })
      );
    };
  }

  private prepareCcEmailAddressTypeahead(): void {
    this.ccEmailAddressTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      const inputFocus$ = this.ccEmailAddressTypeaheadInputFocus$;

      this.ccExistingEmailAddresses.length = 0;

      for (const contact of this.contacts) {
        if (contact.email != null) {
          this.ccExistingEmailAddresses.push(contact.email);
        }
      }

      return merge(debouncedText$, inputFocus$).pipe(
        map((term) => {
          return (term.length === 0
            ? this.ccExistingEmailAddresses
            : this.ccExistingEmailAddresses.filter((element) => {
                return element.toLowerCase().indexOf(term.toLowerCase()) > -1;
              })
          ).slice(0, 10);
        })
      );
    };
  }

  private setSendEmailFormValues(): void {
    for (const contact of this.contacts) {
      if (contact.isPrimary && contact.email !== null) {
        this.toEmailAddresses.push(contact.email);
      }
    }

    this.sendEmailForm.controls.subject.setValue(this.subject);
  }

  addToEmailAddress(event?: NgbTypeaheadSelectItemEvent): void {
    if (event) event.preventDefault();

    const toEmailInput = document.getElementById('SEND_EMAIL_TO') as HTMLInputElement;
    const emailAddress = event ? event.item : toEmailInput.value;

    try {
      if (emailAddress === undefined || emailAddress === null || emailAddress.length === 0) {
        throw new Error('Email cannot be blank.');
      }

      if (this.toEmailAddresses.includes(emailAddress)) {
        throw new Error('Cannot add duplicate email.');
      }

      if (this.toEmailAddresses.length === 10) {
        throw new Error('Email cannot exceed 10 To recipients.');
      }

      if (!this.emailRegex.test(emailAddress)) {
        throw new Error('Email is not formatted correctly.');
      }

      this.toEmailAddresses.push(emailAddress);
      toEmailInput.value = null;
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  }

  removeToEmailAddress(index: number): void {
    const tempArray = [];

    for (let i = 0; i < this.toEmailAddresses.length; i++) {
      if (i !== index) tempArray.push(this.toEmailAddresses[i]);
    }

    this.toEmailAddresses = tempArray;
  }

  addCcEmailAddress(event?: NgbTypeaheadSelectItemEvent): void {
    if (event) event.preventDefault();

    const ccEmailInput = document.getElementById('SEND_EMAIL_CC') as HTMLInputElement;
    const emailAddress = event ? event.item : ccEmailInput.value;

    try {
      if (emailAddress === undefined || emailAddress === null || emailAddress.length === 0) {
        throw new Error('Email cannot be blank.');
      }

      if (this.ccEmailAddresses.includes(emailAddress)) {
        throw new Error('Cannot add duplicate email.');
      }

      if (this.ccEmailAddresses.length === 10) {
        throw new Error('Email cannot exceed 10 Cc recipients.');
      }

      if (!this.emailRegex.test(emailAddress)) {
        throw new Error('Email is not formatted correctly.');
      }

      this.ccEmailAddresses.push(emailAddress);
      ccEmailInput.value = null;
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  }

  removeCcEmailAddress(index: number): void {
    const tempArray = [];

    for (let i = 0; i < this.ccEmailAddresses.length; i++) {
      if (i !== index) tempArray.push(this.ccEmailAddresses[i]);
    }

    this.ccEmailAddresses = tempArray;
  }

  boldSelection(): void {
    document.execCommand('bold');
  }

  sendEmail(): void {
    switch (this.type) {
      case 'PROPOSAL':
        this.sendProposalEmail();
        break;
      case 'INVOICE':
        this.sendInvoiceEmail();
        break;
      case 'PAYMENT':
        this.sendPaymentEmail();
        break;
      case 'REVIEW':
        this.sendReviewEmail();
        break;
      case 'GENERAL':
        this.sendGeneralEmail();
        break;
      default:
        this.alertService.showWarningAlert('Unknown Email Type');
        break;
    }
  }

  private sendProposalEmail(): void {
    try {
      this.alertService.showInfoAlert('Sending...');

      const subject = this.sendEmailForm.value.subject;
      const body = this.sendEmailForm.value.body;

      if (this.toEmailAddresses.length === 0) throw new Error('No recipients have been added.');
      if (!subject) throw new Error('Subject cannot be blank.');
      if (!body) throw new Error('Body cannot be blank.');

      const data = {
        proposalId: this.proposalId,
        to: this.toEmailAddresses,
        cc: this.ccEmailAddresses,
        subject,
        body,
      };

      this.emailService.sendProposalEmail(data).subscribe(() => {
        this.alertService.showSuccessAlert('Email Sent');
        this.modal.close();
        this.emailSent.emit();
      });
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  }

  private sendInvoiceEmail(): void {
    try {
      this.alertService.showInfoAlert('Sending...');

      const subject = this.sendEmailForm.value.subject;
      const body = this.sendEmailForm.value.body;

      if (this.toEmailAddresses.length === 0) throw new Error('No recipients have been added.');
      if (!subject) throw new Error('Subject cannot be blank.');
      if (!body) throw new Error('Body cannot be blank.');

      const data = {
        invoiceId: this.invoiceId,
        to: this.toEmailAddresses,
        cc: this.ccEmailAddresses,
        subject,
        body,
      };

      this.emailService.sendInvoiceEmail(data).subscribe(() => {
        this.alertService.showSuccessAlert('Email Sent');
        this.modal.close();
        this.emailSent.emit();
      });
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  }

  private sendPaymentEmail(): void {
    try {
      this.alertService.showInfoAlert('Sending...');

      const subject = this.sendEmailForm.value.subject;
      const body = this.sendEmailForm.value.body;

      if (this.toEmailAddresses.length === 0) throw new Error('No recipients have been added.');
      if (!subject) throw new Error('Subject cannot be blank.');
      if (!body) throw new Error('Body cannot be blank.');

      const data = {
        paymentId: this.paymentId,
        to: this.toEmailAddresses,
        cc: this.ccEmailAddresses,
        subject,
        body,
      };

      this.emailService.sendPaymentEmail(data).subscribe(() => {
        this.alertService.showSuccessAlert('Email Sent');
        this.modal.close();
        this.emailSent.emit();
      });
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  }

  private sendReviewEmail(): void {
    try {
      this.alertService.showInfoAlert('Sending...');

      const subject = this.sendEmailForm.value.subject;
      const body = this.sendEmailForm.value.body;

      if (this.toEmailAddresses.length === 0) throw new Error('No recipients have been added.');
      if (!subject) throw new Error('Subject cannot be blank.');
      if (!body) throw new Error('Body cannot be blank.');

      const data = {
        projectId: this.projectId,
        to: this.toEmailAddresses,
        cc: this.ccEmailAddresses,
        subject,
        body,
      };

      this.emailService.sendReviewEmail(data).subscribe(() => {
        this.alertService.showSuccessAlert('Email Sent');
        this.modal.close();
        this.emailSent.emit();
      });
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  }

  private sendGeneralEmail(): void {
    try {
      this.alertService.showInfoAlert('Sending...');

      const subject = this.sendEmailForm.value.subject;
      const body = this.sendEmailForm.value.body;

      if (this.toEmailAddresses.length === 0) throw new Error('No recipients have been added.');
      if (!subject) throw new Error('Subject cannot be blank.');
      if (!body) throw new Error('Body cannot be blank.');

      const data = {
        to: this.toEmailAddresses,
        cc: this.ccEmailAddresses,
        subject,
        body,
      };

      this.emailService.sendGenericEmail(data).subscribe(() => {
        this.alertService.showSuccessAlert('Email Sent');
        this.modal.close();
        this.emailSent.emit();
      });
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  }

  resetSendEmailForm(): void {
    this.sendEmailForm.reset();
    this.toEmailAddresses.length = 0;
    this.ccEmailAddresses.length = 0;

    for (const contact of this.contacts) {
      if (contact.isPrimary && contact.email) {
        this.toEmailAddresses.push(contact.email);
      }
    }

    this.sendEmailForm.controls.subject.setValue(this.subject);
    this.getEmailTemplates();
  }

  get sendEmailSubject() {
    return this.sendEmailForm.controls.subject;
  }
}